import React, { createContext, useState, useCallback, useEffect } from 'react';

import deleteCustomerFavList from '@smart-tv/utils/kmm/deleteCustomerFavList';
import getCustomerFavoritListsWithSidsKmm from '@smart-tv/utils/kmm/getCustomerFavoritListsWithSids';
import saveCustomerFavList from '@smart-tv/utils/kmm/saveCustomerFavList';
import {
  retrievePersistentData,
  storePersistentData,
  removePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

export interface FavoritesList {
  id: number;
  name: string;
  sids: number[];
}

export const DEFAULT_FAVORITES_LIST = {
  id: -2,
  name: 'Alle kanaler',
  sids: []
};

export interface FavoritesListsContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface FavoritesListsContextValues {
  favoritLists?: FavoritesList[];
  onDeleteFavoritesList: (favoritesList: FavoritesList) => Promise<void>;
  onSaveFavoritesList: (favoritesList: FavoritesList) => Promise<void>;
  onSelectFavoritesList: (favoritesList: FavoritesList) => Promise<void>;
  selectedFavoritesList?: FavoritesList;
  getFavoritesLists: () => Promise<FavoritesList[]>;
}

export const FavoritesListsContext = createContext<FavoritesListsContextValues>(
  {} as FavoritesListsContextValues
);

export const FavoritesListsConsumer = FavoritesListsContext.Consumer;

export const FavoritesListsProvider = (props: FavoritesListsContextProps): JSX.Element => {
  const { children } = props;

  const [favoritLists, setFavoritLists] = useState<FavoritesList[]>();

  const storedFavoritesList = retrievePersistentData<FavoritesList>('favorites-list');

  const [selectedFavoritesList, setSelectedFavoritesList] = useState<FavoritesList>();

  const handleGetFavoritesLists = useCallback(async () => {
    const favoritLists = await getCustomerFavoritListsWithSidsKmm();

    setFavoritLists(favoritLists);

    return favoritLists;
  }, []);

  const handleSelectFavoritesList = useCallback(
    async (favoritesList: FavoritesList) => {
      storePersistentData('favorites-list', favoritesList);
      setSelectedFavoritesList(favoritesList);
    },
    [setSelectedFavoritesList]
  );

  const handleDeleteFavoritesList = useCallback(
    async (favoritesList: FavoritesList) => {
      const { id } = favoritesList;

      await deleteCustomerFavList(id);

      await handleGetFavoritesLists();

      if (id === selectedFavoritesList?.id) {
        setSelectedFavoritesList(DEFAULT_FAVORITES_LIST);
      }
    },
    [handleGetFavoritesLists, selectedFavoritesList?.id]
  );

  const handleSaveFavoritesList = useCallback(
    async (favoritesList: FavoritesList) => {
      try {
        if (favoritesList.sids.length === 0) {
          await handleDeleteFavoritesList(favoritesList);
          return;
        }
        await saveCustomerFavList(favoritesList);

        const updatedFavoritesLists = await handleGetFavoritesLists();
        const savedFavoritesList =
          updatedFavoritesLists.find((list: FavoritesList) => list.id === favoritesList.id) ||
          updatedFavoritesLists.find((list: FavoritesList) => list.name === favoritesList.name) ||
          favoritesList;

        await handleSelectFavoritesList(savedFavoritesList);
      } catch (error) {
        console.error('Error saving favorites list:', error);
      }
    },
    [handleGetFavoritesLists, handleSelectFavoritesList, handleDeleteFavoritesList]
  );

  // GET FAVORIT LISTS - Get the favorit lists on first render
  useEffect(() => {
    handleGetFavoritesLists();
  }, [handleGetFavoritesLists]);

  // SELECTED FAVORIT LIST - set the selected favorit list, when the lists are fetch or each time the stored favorit list changes
  useEffect(() => {
    // Only use stored list if we have no selected list yet, and if the stored list is still in the fetched lists
    if (selectedFavoritesList || !favoritLists) return;

    const selectedFavoritList = favoritLists.find(
      ({ id, sids }) => id === storedFavoritesList?.id && sids.length
    );

    if (selectedFavoritList) {
      setSelectedFavoritesList(selectedFavoritList);
      return;
    }

    removePersistentData('favorites-list');

    setSelectedFavoritesList(DEFAULT_FAVORITES_LIST);
  }, [favoritLists, storedFavoritesList, selectedFavoritesList?.id, selectedFavoritesList]);

  return (
    <FavoritesListsContext.Provider
      value={{
        favoritLists,
        selectedFavoritesList,
        onSaveFavoritesList: handleSaveFavoritesList,
        onDeleteFavoritesList: handleDeleteFavoritesList,
        onSelectFavoritesList: handleSelectFavoritesList,
        getFavoritesLists: handleGetFavoritesLists
      }}
    >
      {children}
    </FavoritesListsContext.Provider>
  );
};

export default FavoritesListsContext;
